import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw, Send, AlertCircle, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  return String(detail);
}

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-slate-400 outline-none transition-colors focus:border-cyan-400/60 focus:ring-2 focus:ring-cyan-400/20";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso || "—";
  }
}

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("edl_admin_token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadLeads = async (t) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setLeads(data);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("edl_admin_token");
        setToken("");
      }
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  const exportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    setError("");
    try {
      const resp = await axios.get(`${API}/admin/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `demandes-edl-express-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Export impossible — reconnectez-vous.");
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (token) loadLeads(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("edl_admin_token", data.token);
      setToken(data.token);
      loadLeads(data.token);
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail));
    }
  };

  const logout = () => {
    sessionStorage.removeItem("edl_admin_token");
    setToken("");
    setLeads([]);
    setPassword("");
  };

  return (
    <div className="min-h-screen bg-night text-white">
      <div className="mx-auto max-w-7xl px-5 py-10 md:px-8">
        <div className="flex items-center justify-between">
          <img
            src="/logo.webp"
            alt="EDL-EXPRESS — Espace Pro"
            className="h-10 w-auto"
          />
          {token ? (
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-cyan-400/60 hover:text-cyan-300"
            >
              <LogOut className="h-4 w-4" />
              Déconnexion
            </button>
          ) : null}
        </div>

        {error ? (
          <div
            data-testid="admin-error-alert"
            className="mt-6 flex items-center gap-2.5 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
          </div>
        ) : null}

        {!token ? (
          <div className="mx-auto mt-16 max-w-md">
            <div className="rounded-3xl border border-white/10 bg-elevated/70 p-8">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl border border-cyan-400/25 bg-cyan-400/10 text-cyan-300">
                <Lock className="h-5 w-5" />
              </span>
              <h1 className="mt-5 font-display text-2xl font-bold text-white">Espace Pro</h1>
              <p className="mt-2 text-sm text-slate-400">
                Accès réservé — consultez les demandes de disponibilité reçues.
              </p>
              <form onSubmit={login} className="mt-6 space-y-4">
                <input
                  type="password"
                  data-testid="admin-password-input"
                  className={inputCls}
                  placeholder="Mot de passe administrateur"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="submit"
                  data-testid="admin-login-btn"
                  className="w-full rounded-full bg-gradient-to-r from-cyan-400 to-electric px-7 py-3 text-sm font-semibold text-night transition-all duration-300 hover:shadow-[0_0_28px_rgba(34,211,238,0.45)]"
                >
                  Se connecter
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="mt-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="font-display text-2xl font-bold text-white">
                  Demandes reçues
                  <span className="ml-3 font-mono text-sm font-medium text-cyan-400">
                    {leads.length}
                  </span>
                </h1>
                <p className="mt-1 text-sm text-slate-400">
                  Toutes les demandes de disponibilité envoyées depuis le site.
                </p>
              </div>
              <button
                data-testid="admin-export-btn"
                onClick={exportExcel}
                disabled={exporting}
                className="flex items-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-electric px-4 py-2 text-sm font-semibold text-night transition-all duration-300 hover:shadow-[0_0_24px_rgba(34,211,238,0.45)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Download className={`h-4 w-4 ${exporting ? "animate-bounce" : ""}`} />
                {exporting ? "Préparation…" : "Exporter en Excel"}
              </button>
              <button
                data-testid="admin-refresh-btn"
                onClick={() => loadLeads(token)}
                className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-sm text-slate-300 transition-colors hover:border-cyan-400/60 hover:text-cyan-300"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                Actualiser
              </button>
            </div>

            <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10">
              <table data-testid="admin-leads-table" className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-white/10 bg-white/[0.03]">
                    {["Reçue le", "Nom / Agence", "Contact", "Bien", "État des lieux", "Ville", "Souhaitée", "Message", "E-mail"].map(
                      (h) => (
                        <th
                          key={h}
                          className="px-4 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400"
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr
                      key={l.id}
                      className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.03]"
                    >
                      <td className="px-4 py-3 align-top font-mono text-xs text-slate-400">
                        {formatDate(l.created_at)}
                      </td>
                      <td className="px-4 py-3 align-top font-semibold text-white">{l.full_name}</td>
                      <td className="px-4 py-3 align-top text-slate-300">
                        <a href={`tel:${l.phone}`} className="block hover:text-cyan-300">
                          {l.phone}
                        </a>
                        <a href={`mailto:${l.email}`} className="block text-xs text-slate-400 hover:text-cyan-300">
                          {l.email}
                        </a>
                      </td>
                      <td className="px-4 py-3 align-top text-slate-300">{l.property_type}</td>
                      <td className="px-4 py-3 align-top text-slate-300">{l.edl_type}</td>
                      <td className="px-4 py-3 align-top text-slate-300">{l.city}</td>
                      <td className="px-4 py-3 align-top text-slate-300">{l.desired_date || "—"}</td>
                      <td className="max-w-[220px] px-4 py-3 align-top text-xs text-slate-400">
                        {l.message || "—"}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {l.email_sent ? (
                          <span className="flex items-center gap-1.5 rounded-full border border-neon/30 bg-neon/10 px-2.5 py-0.5 font-mono text-[10px] text-neon">
                            <Send className="h-3 w-3" />
                            Envoyé
                          </span>
                        ) : (
                          <span className="rounded-full border border-white/15 bg-white/5 px-2.5 py-0.5 font-mono text-[10px] text-slate-400">
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {leads.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-4 py-10 text-center text-slate-400">
                        Aucune demande pour le moment.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
